import { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import { useQuery, useQueryClient } from '@tanstack/react-query'

import api from 'services/api'
import { trackEvent } from 'services/analytics'

import { useItineraries } from 'contexts/itinerary.context'
import { useUser } from 'contexts/user.context'

import { ModelItineraries } from 'models/ModelItineraries'
import { getItineraryPublicPath } from 'utils/itineraryUrl'

import ConfirmModal from 'components/confirmModal'
import LoginModal from 'components/modal'
import StarRating from 'components/starRating'
import ItineraryComments from 'components/itineraryComments'

import { Container, Content, Header, Line } from './style'
import * as S from './style'

type LightboxState = {
  images: string[]
  currentIndex: number
  cityName: string
}

type InspirationStep =
  | 'form'
  | 'planning'
  | 'structure'
  | 'processing'
  | 'error'
  | 'ready'

type InspirationStructureChoice = 'day_trip' | 'replace_day' | 'add_day'

type InspirationStructurePlan = {
  status: 'ready' | 'clarification'
  clarificationType: 'none' | 'structure' | 'place' | 'viability'
  clarificationOptions: Array<{
    value: string
    label: string
    description: string
  }>
  question: string
  requestedPlace: string
  baseCity: string
  selectedAction: 'none' | 'day_trip' | 'replace_day' | 'add_day' | 'reshape'
  originalDays: number
  proposedDays: number
  changes: string[]
  days: Array<{
    dayNumber: number
    cities: string[]
    note: string
  }>
  reason: string
}

type InspirationPreferences = {
  keep: string
  change: string
  company: 'solo' | 'couple' | 'friends' | 'family'
  pace: 'slow' | 'balanced' | 'intense'
}

const MAX_INSPIRATION_CHARS = 2000

type InspirationFailureStage = 'planning' | 'generation'

const initialInspirationPreferences: InspirationPreferences = {
  keep: '',
  change: '',
  company: 'couple',
  pace: 'balanced'
}

const companyLabels: Record<InspirationPreferences['company'], string> = {
  solo: 'Sozinho',
  couple: 'Casal',
  friends: 'Amigos',
  family: 'Com crianças'
}

const paceLabels: Record<InspirationPreferences['pace'], string> = {
  slow: 'Tranquilo',
  balanced: 'Equilibrado',
  intense: 'Quero aproveitar tudo'
}

const absoluteUrl = (value?: string | null) => {
  const url = String(value || '').trim()
  if (!url) return ''
  if (/^https?:\/\//i.test(url)) return url
  return `https://redsterna.com.br/${url.replace(/^\/+/, '')}`
}

const shouldNoIndexHost = () => {
  const hostname = window.location.hostname.toLowerCase()
  return hostname.endsWith('.up.railway.app') || hostname.includes('homolog')
}

const setSeoMeta = (
  selector: string,
  attributes: Record<string, string>,
  content: string
) => {
  let element = document.head.querySelector(selector) as HTMLMetaElement | null
  if (!element) {
    element = document.createElement('meta')
    Object.entries(attributes).forEach(([key, value]) =>
      element?.setAttribute(key, value)
    )
    document.head.appendChild(element)
  }
  element.setAttribute('content', content)
}

export default function ShowItinerary() {
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [isInspirationModalOpen, setIsInspirationModalOpen] = useState(false)
  const [isInspiring, setIsInspiring] = useState(false)
  const [inspirationStep, setInspirationStep] =
    useState<InspirationStep>('form')
  const [inspirationPreferences, setInspirationPreferences] =
    useState<InspirationPreferences>(initialInspirationPreferences)
  const [inspiredDraftId, setInspiredDraftId] = useState<string | null>(null)
  const [inspirationTags, setInspirationTags] = useState<string[]>([])
  const [verifiedPlacesCount, setVerifiedPlacesCount] = useState(0)
  const [inspirationProgress, setInspirationProgress] = useState(0)
  const [structurePlan, setStructurePlan] =
    useState<InspirationStructurePlan | null>(null)
  const [selectedStructureAction, setSelectedStructureAction] =
    useState<InspirationStructureChoice>('day_trip')
  const [selectedClarificationAnswer, setSelectedClarificationAnswer] =
    useState('')
  const [inspirationError, setInspirationError] = useState('')
  const [inspirationFailureStage, setInspirationFailureStage] =
    useState<InspirationFailureStage>('planning')
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [lightbox, setLightbox] = useState<LightboxState | null>(null)

  const { id: legacyId, shortId } = useParams<{
    id?: string
    shortId?: string
  }>()
  const history = useHistory()
  const location = useLocation()
  const routeIdentifier = shortId || legacyId || ''
  const queryClient = useQueryClient()
  const { user } = useUser()
  const { favoriteItinerary, rateItinerary } = useItineraries()

  const {
    isLoading,
    isError,
    error,
    data: itinerary,
    refetch
  } = useQuery({
    queryKey: ['Itineraries', shortId ? 'short' : 'uuid', routeIdentifier],
    queryFn: async () => {
      if (!routeIdentifier) throw new Error('Roteiro inválido')

      const endpoint = shortId
        ? `/itineraries/short/${routeIdentifier}`
        : `/itineraries/${routeIdentifier}`
      const { data }: { data: ModelItineraries } = await api.get(endpoint)

      if (!data?.id) {
        throw new Error('Roteiro indisponível')
      }

      const { rates } = data
      const validRates = rates?.filter(
        (rating: any) => rating.userId !== data.authorId
      )
      const rate = validRates?.length
        ? validRates.reduce((a: number, b: { rate: number }) => a + b.rate, 0) /
          validRates.length
        : 0

      return { rate, ...data }
    },
    enabled: !!routeIdentifier,
    retry: false,
    staleTime: 15 * 60 * 1000
  })

  const id = itinerary?.id || legacyId || ''

  useEffect(() => {
    if (!itinerary?.id) return

    const canonicalPath = getItineraryPublicPath(itinerary)
    if (location.pathname !== canonicalPath || location.search) {
      history.replace(canonicalPath)
    }
  }, [history, itinerary, location.pathname, location.search])

  const {
    data: favoriteItineraries = [],
    refetch: refetchFavoriteItineraries
  } = useQuery({
    queryKey: ['favoriteItineraries', user?.id],
    queryFn: async () => {
      if (!user?.id) return []
      const { data } = await api.get(`/itineraries/user/favorite/${user.id}`)
      return Array.isArray(data) ? data : []
    },
    enabled: !!user?.id,
    staleTime: 60 * 1000
  })

  const { data: inspirationStatus, refetch: refetchInspirationStatus } =
    useQuery({
      queryKey: ['inspirationStatus', id, user?.id],
      queryFn: async () => {
        const response = await api.get(`/itineraries/${id}/inspiration-status`)
        return response.data
      },
      enabled: !!user?.id && !!id,
      staleTime: 0,
      refetchOnMount: 'always',
      refetchOnWindowFocus: true
    })

  const authenticated = !!user
  const isOwnItinerary = !!user?.id && itinerary?.authorId === user.id
  const hasInspirationDraft =
    inspirationStep !== 'ready' &&
    Boolean(
      inspirationPreferences.keep.trim() ||
        inspirationPreferences.change.trim() ||
        structurePlan
    )

  useEffect(() => {
    if (!itinerary?.id) return

    trackEvent({
      eventType: 'itinerary_view',
      entityType: 'itinerary',
      entityId: itinerary.id,
      value: itinerary.title
    })
  }, [itinerary?.id, itinerary?.title])
  useEffect(() => {
    if (isLoading) return

    const previousTitle = document.title
    const previousRobots =
      document.head
        .querySelector('meta[name="robots"]')
        ?.getAttribute('content') || ''

    if (isError || !itinerary?.id) {
      document.title = 'Roteiro indisponível | RedSterna'
      setSeoMeta('meta[name="robots"]', { name: 'robots' }, 'noindex, nofollow')

      return () => {
        document.title = previousTitle
        if (previousRobots) {
          setSeoMeta('meta[name="robots"]', { name: 'robots' }, previousRobots)
        } else {
          document.head.querySelector('meta[name="robots"]')?.remove()
        }
      }
    }

    const cities = Array.from(
      new Set(
        (itinerary.cities || [])
          .map((city: any) => String(city?.name || '').trim())
          .filter(Boolean)
      )
    )
    const cityLabel = cities.slice(0, 3).join(', ')
    const days = Math.max(Number(itinerary.days) || 1, 1)
    const summary = String(itinerary.summary || '')
      .replace(/\s+/g, ' ')
      .trim()
    const fallbackDescription = cityLabel
      ? `Roteiro de ${days} ${days === 1 ? 'dia' : 'dias'} por ${cityLabel}, com experiências, cidades e dicas compartilhadas na RedSterna.`
      : `Confira este roteiro de ${days} ${days === 1 ? 'dia' : 'dias'} na RedSterna e inspire-se para planejar sua próxima viagem.`
    const description = (summary || fallbackDescription).slice(0, 160)
    const titleBase = cityLabel
      ? `${itinerary.title} — ${cityLabel}`
      : itinerary.title
    const title = `${titleBase} | RedSterna`.slice(0, 70)
    const canonical = `https://redsterna.com.br${getItineraryPublicPath(itinerary)}`
    const image =
      absoluteUrl(itinerary.coverImage) ||
      'https://redsterna.com.br/images/thumbnail.jpg'
    const authorName = itinerary.author
      ? `${itinerary.author.firstname || ''} ${itinerary.author.lastname || ''}`.trim()
      : 'RedSterna'

    document.title = title
    setSeoMeta('meta[name="description"]', { name: 'description' }, description)
    setSeoMeta(
      'meta[name="robots"]',
      { name: 'robots' },
      shouldNoIndexHost() ? 'noindex, nofollow' : 'index, follow'
    )
    setSeoMeta('meta[property="og:title"]', { property: 'og:title' }, title)
    setSeoMeta(
      'meta[property="og:description"]',
      { property: 'og:description' },
      description
    )
    setSeoMeta('meta[property="og:type"]', { property: 'og:type' }, 'article')
    setSeoMeta('meta[property="og:url"]', { property: 'og:url' }, canonical)
    setSeoMeta('meta[property="og:image"]', { property: 'og:image' }, image)
    setSeoMeta(
      'meta[name="twitter:card"]',
      { name: 'twitter:card' },
      'summary_large_image'
    )
    setSeoMeta('meta[name="twitter:title"]', { name: 'twitter:title' }, title)
    setSeoMeta(
      'meta[name="twitter:description"]',
      { name: 'twitter:description' },
      description
    )
    setSeoMeta('meta[name="twitter:image"]', { name: 'twitter:image' }, image)

    let canonicalElement = document.head.querySelector(
      'link[rel="canonical"]'
    ) as HTMLLinkElement | null
    if (!canonicalElement) {
      canonicalElement = document.createElement('link')
      canonicalElement.rel = 'canonical'
      document.head.appendChild(canonicalElement)
    }
    canonicalElement.href = canonical

    const scriptId = 'redsterna-itinerary-structured-data'
    document.getElementById(scriptId)?.remove()
    const script = document.createElement('script')
    script.id = scriptId
    script.type = 'application/ld+json'
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebPage',
          '@id': canonical + '#webpage',
          url: canonical,
          name: title,
          description,
          inLanguage: 'pt-BR',
          datePublished: itinerary.createdAt || undefined,
          dateModified: itinerary.updatedAt || undefined,
          isPartOf: {
            '@id': 'https://redsterna.com.br/#website'
          },
          publisher: {
            '@id': 'https://redsterna.com.br/#organization'
          },
          mainEntity: {
            '@id': canonical + '#trip'
          }
        },
        {
          '@type': 'TouristTrip',
          '@id': canonical + '#trip',
          name: itinerary.title,
          description,
          url: canonical,
          image,
          touristType:
            itinerary.semanticProfile?.audiences?.length ||
            itinerary.semanticProfile?.themes?.length
              ? [
                  ...(itinerary.semanticProfile?.audiences || []),
                  ...(itinerary.semanticProfile?.themes || [])
                ].slice(0, 10)
              : (itinerary.interests || []).slice(0, 8),
          keywords: itinerary.semanticProfile?.labels?.join(', ') || undefined,
          duration: itinerary.semanticProfile?.durationDays
            ? `P${itinerary.semanticProfile.durationDays}D`
            : undefined,
          additionalProperty: itinerary.semanticProfile
            ? [
                {
                  '@type': 'PropertyValue',
                  name: 'Perfil de viagem',
                  value:
                    itinerary.semanticProfile.audiences.join(', ') ||
                    'não inferido'
                },
                {
                  '@type': 'PropertyValue',
                  name: 'Ritmo',
                  value: itinerary.semanticProfile.pace || 'não inferido'
                },
                {
                  '@type': 'PropertyValue',
                  name: 'Temas',
                  value:
                    itinerary.semanticProfile.themes.join(', ') ||
                    'não inferido'
                }
              ]
            : undefined,
          itinerary: cities.map((name) => ({
            '@type': 'TouristDestination',
            name
          })),
          author: itinerary.isEditorial
            ? {
                '@id': 'https://redsterna.com.br/#organization'
              }
            : {
                '@type': 'Person',
                name: authorName
              }
        },
        {
          '@type': 'BreadcrumbList',
          '@id': canonical + '#breadcrumb',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'RedSterna',
              item: 'https://redsterna.com.br/'
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'Roteiros',
              item: 'https://redsterna.com.br/roteiros'
            },
            {
              '@type': 'ListItem',
              position: 3,
              name: itinerary.title,
              item: canonical
            }
          ]
        }
      ]
    })
    document.head.appendChild(script)

    return () => {
      document.getElementById(scriptId)?.remove()
      document.title = previousTitle
      if (previousRobots) {
        setSeoMeta('meta[name="robots"]', { name: 'robots' }, previousRobots)
      } else {
        document.head.querySelector('meta[name="robots"]')?.remove()
      }
    }
  }, [isError, isLoading, itinerary])

  const closeLightbox = useCallback(() => setLightbox(null), [])

  const changeLightboxImage = useCallback((direction: number) => {
    setLightbox((current) => {
      if (!current || current.images.length <= 1) return current
      const nextIndex =
        (current.currentIndex + direction + current.images.length) %
        current.images.length
      return { ...current, currentIndex: nextIndex }
    })
  }, [])

  useEffect(() => {
    if (!lightbox) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeLightbox()
      if (event.key === 'ArrowLeft') changeLightboxImage(-1)
      if (event.key === 'ArrowRight') changeLightboxImage(1)
    }

    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', handleKeyDown)

    return () => {
      document.body.style.overflow = ''
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [changeLightboxImage, closeLightbox, lightbox])

  const onFavoriteItinerary = useCallback(
    async (id: string) => {
      if (!user) {
        setIsModalVisible(true)
        return
      }

      if (isOwnItinerary) {
        toast.info('Você não pode favoritar seu próprio roteiro.', {
          theme: 'dark'
        })
        return
      }

      await favoriteItinerary(id)
      await refetchFavoriteItineraries()
    },
    [favoriteItinerary, isOwnItinerary, refetchFavoriteItineraries, user]
  )

  const onRateItinerary = useCallback(
    async ({ id, rate }: { id: string; rate: number }) => {
      if (!user) {
        setIsModalVisible(true)
        return
      }

      if (isOwnItinerary) {
        toast.info('Você não pode avaliar seu próprio roteiro.', {
          theme: 'dark'
        })
        return
      }

      try {
        await rateItinerary({ id, rate })
        await refetch()
        toast.success('Roteiro avaliado!', { theme: 'dark' })
      } catch {
        toast.error('Não foi possível avaliar este roteiro.', { theme: 'dark' })
      }
    },
    [isOwnItinerary, rateItinerary, refetch, user]
  )

  useEffect(() => {
    if (!user?.id || !id) return

    const pendingItineraryId = window.localStorage.getItem(
      'pendingInspirationItineraryId'
    )

    if (pendingItineraryId === id) {
      window.localStorage.removeItem('pendingInspirationItineraryId')
      setInspirationStep('form')
      setIsInspirationModalOpen(true)
    }
  }, [id, user?.id])

  useEffect(() => {
    if (inspirationStep !== 'processing') return undefined

    const startedAt = Date.now()
    setInspirationProgress((current) => Math.max(current, 6))

    const timer = window.setInterval(() => {
      const elapsedSeconds = (Date.now() - startedAt) / 1000

      let estimated = 6
      if (elapsedSeconds <= 8) {
        estimated = 6 + (elapsedSeconds / 8) * 34
      } else if (elapsedSeconds <= 20) {
        estimated = 40 + ((elapsedSeconds - 8) / 12) * 28
      } else if (elapsedSeconds <= 40) {
        estimated = 68 + ((elapsedSeconds - 20) / 20) * 20
      } else if (elapsedSeconds <= 70) {
        estimated = 88 + ((elapsedSeconds - 40) / 30) * 7
      } else {
        estimated = 95
      }

      setInspirationProgress((current) =>
        Math.max(current, Math.min(95, Math.round(estimated)))
      )
    }, 500)

    return () => window.clearInterval(timer)
  }, [inspirationStep])

  const requestInspiration = () => {
    if (!user) {
      window.localStorage.setItem('pendingInspirationItineraryId', id)
      setIsModalVisible(true)
      return
    }

    if (!hasInspirationDraft) {
      setInspirationStep('form')
      setInspirationPreferences(initialInspirationPreferences)
      setInspiredDraftId(inspirationStatus?.inspiredItineraryId ?? null)
      setInspirationTags([])
      setVerifiedPlacesCount(0)
      setInspirationProgress(0)
      setStructurePlan(null)
      setSelectedStructureAction('day_trip')
      setSelectedClarificationAnswer('')
      setInspirationError('')
      setInspirationFailureStage('planning')
    }

    setIsInspirationModalOpen(true)

    trackEvent({
      eventType: 'itinerary_personalization_opened',
      entityType: 'itinerary',
      entityId: id
    })
  }

  const generateInspiredItinerary = async (
    confirmedStructurePlan?: InspirationStructurePlan
  ) => {
    if (!id || !itinerary) return

    setInspirationTags([])
    setVerifiedPlacesCount(0)
    setInspirationProgress(6)
    setInspirationError('')
    setInspirationFailureStage('generation')
    setInspirationStep('processing')
    setIsInspiring(true)

    trackEvent({
      eventType: 'itinerary_personalization_started',
      entityType: 'itinerary',
      entityId: id,
      metadata: {
        company: inspirationPreferences.company,
        pace: inspirationPreferences.pace,
        structureAction: confirmedStructurePlan?.selectedAction ?? 'none'
      }
    })

    try {
      const response = await api.post(
        `/itineraries/${id}/personalize`,
        {
          ...inspirationPreferences,
          structurePlan: confirmedStructurePlan
        },
        { timeout: 170000 }
      )

      const inspiredId = response.data.id as string
      const tags = Array.isArray(response.data.focusTags)
        ? response.data.focusTags
        : []
      const verifiedCount = Number(response.data.verifiedPlacesCount || 0)

      await Promise.all([
        refetchInspirationStatus(),
        queryClient.invalidateQueries({
          queryKey: ['itinerariesUser', user?.id]
        }),
        queryClient.invalidateQueries({
          queryKey: ['Itineraries', inspiredId]
        }),
        queryClient.invalidateQueries({
          queryKey: ['AllItineraries']
        })
      ])

      setInspiredDraftId(inspiredId)
      setInspirationTags(tags)
      setVerifiedPlacesCount(verifiedCount)
      setStructurePlan(confirmedStructurePlan ?? null)
      setInspirationProgress(100)
      await new Promise((resolve) => window.setTimeout(resolve, 350))
      setInspirationStep('ready')

      trackEvent({
        eventType: 'itinerary_personalization_ai_completed',
        entityType: 'itinerary',
        entityId: inspiredId,
        metadata: {
          sourceItineraryId: id,
          company: inspirationPreferences.company,
          pace: inspirationPreferences.pace,
          structureAction: confirmedStructurePlan?.selectedAction ?? 'none',
          inferredTags: tags,
          verifiedPlacesCount: verifiedCount
        }
      })
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        (error?.code === 'ECONNABORTED'
          ? 'A criação demorou mais do que o esperado. Seu pedido foi preservado e você pode tentar novamente.'
          : 'Não foi possível criar sua versão deste roteiro agora.')

      setInspirationProgress(0)
      setInspirationError(message)
      setInspirationFailureStage('generation')
      setInspirationStep('error')
      toast.error(message, { theme: 'dark' })
    } finally {
      setIsInspiring(false)
    }
  }

  const planPersonalizedItinerary = async (
    structureChoice?: InspirationStructureChoice,
    clarificationAnswer?: string
  ) => {
    if (!id || !itinerary || isInspiring) return

    setInspirationError('')
    setInspirationFailureStage('planning')
    setInspirationStep('planning')
    setIsInspiring(true)

    try {
      const response = await api.post<InspirationStructurePlan>(
        `/itineraries/${id}/personalization-plan`,
        {
          ...inspirationPreferences,
          structureChoice,
          clarificationAnswer
        },
        { timeout: 75000 }
      )

      const plan = response.data
      setStructurePlan(plan)

      if (plan.status === 'clarification') {
        if (plan.clarificationType === 'structure') {
          setSelectedStructureAction(structureChoice || 'day_trip')
        } else {
          setSelectedClarificationAnswer('')
        }
        setInspirationStep('structure')
        return
      }

      const hasStructuralChanges =
        (plan.changes?.length ?? 0) > 0 ||
        plan.proposedDays !== plan.originalDays ||
        plan.selectedAction !== 'none'

      if (hasStructuralChanges) {
        setInspirationStep('structure')
        return
      }

      await generateInspiredItinerary(plan)
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        (error?.code === 'ECONNABORTED'
          ? 'A interpretação demorou mais do que o esperado. Seu pedido foi preservado e você pode tentar novamente.'
          : 'Não foi possível interpretar sua personalização agora.')

      setInspirationError(message)
      setInspirationFailureStage('planning')
      setInspirationStep('error')
      toast.error(message, { theme: 'dark' })
    } finally {
      setIsInspiring(false)
    }
  }

  const createInspiredItinerary = async () => {
    if (!id || !itinerary || isInspiring) return

    const combinedInput =
      `${inspirationPreferences.keep} ${inspirationPreferences.change}`.trim()

    if (combinedInput.length < 10) {
      toast.info(
        'Conte um pouco do que você quer manter ou mudar para criarmos sua versão.',
        { theme: 'dark' }
      )
      return
    }

    await planPersonalizedItinerary()
  }

  const retryInspiration = async () => {
    if (isInspiring) return

    if (inspirationFailureStage === 'generation' && structurePlan) {
      await generateInspiredItinerary(structurePlan)
      return
    }

    await planPersonalizedItinerary()
  }

  const confirmStructureAndCreate = async () => {
    if (!structurePlan || isInspiring) return

    if (structurePlan.status === 'clarification') {
      if (structurePlan.clarificationType === 'structure') {
        await planPersonalizedItinerary(selectedStructureAction)
        return
      }

      if (!selectedClarificationAnswer) {
        toast.info('Escolha uma opção para continuarmos.', { theme: 'dark' })
        return
      }

      const confirmedStructureChoice =
        structurePlan.selectedAction === 'day_trip' ||
        structurePlan.selectedAction === 'replace_day' ||
        structurePlan.selectedAction === 'add_day'
          ? structurePlan.selectedAction
          : undefined

      await planPersonalizedItinerary(
        confirmedStructureChoice,
        selectedClarificationAnswer
      )
      return
    }

    await generateInspiredItinerary(structurePlan)
  }

  const openInspiredDraft = () => {
    if (!inspiredDraftId) return

    setIsInspirationModalOpen(false)
    history.push(`/editar-roteiro/${inspiredDraftId}`)
  }

  const deleteOwnItinerary = async () => {
    if (!itinerary || !isOwnItinerary || isDeleting) return

    setIsDeleting(true)

    try {
      await api.delete(`/itineraries/${itinerary.id}`)
      setIsDeleteModalOpen(false)
      toast.success('Roteiro excluído com sucesso.', { theme: 'dark' })

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['itinerariesUser', user?.id]
        }),
        queryClient.invalidateQueries({
          queryKey: ['favoriteItineraries', user?.id]
        }),
        queryClient.invalidateQueries({ queryKey: ['AllItineraries'] }),
        queryClient.invalidateQueries({ queryKey: ['Itineraries'] }),
        queryClient.invalidateQueries({ queryKey: ['inspirationStatus'] })
      ])

      history.push('/minha-conta')
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || 'Não foi possível excluir o roteiro.',
        { theme: 'dark' }
      )
    } finally {
      setIsDeleting(false)
    }
  }

  const isFavorited = useMemo(() => {
    if (authenticated && itinerary) {
      return favoriteItineraries.some((favorite: any) => {
        const favoriteId = favorite?.itinerary?.id ?? favorite?.id
        return favoriteId === itinerary.id
      })
    }
    return false
  }, [authenticated, favoriteItineraries, itinerary])

  const isMobile = window.innerWidth < 960

  if (isLoading) return <h1>Carregando roteiro...</h1>

  if (isError || !itinerary?.id) {
    const status = Number((error as any)?.response?.status)
    const message =
      status === 404
        ? 'Este roteiro não está disponível para visualização. Ele pode ser privado, planejado ou ter sido removido.'
        : 'Não foi possível carregar este roteiro.'

    return (
      <Container>
        <Content>
          <h1>Roteiro indisponível</h1>
          <p>{message}</p>
          <button type="button" onClick={() => history.push('/roteiros')}>
            Voltar para roteiros
          </button>
        </Content>
      </Container>
    )
  }

  const userRate = authenticated
    ? (itinerary.rates as any[] | undefined)?.find(
        (rating: any) => rating.userId === user?.id
      )?.rate
    : undefined
  const displayedRate = isOwnItinerary
    ? itinerary.rate
    : userRate ?? itinerary.rate
  const hasSummary = Boolean(itinerary.summary?.trim())
  const dayPlanSource =
    itinerary.dayPlan && itinerary.dayPlan.length > 0
      ? itinerary.dayPlan
      : Array.from(
          { length: Math.max(Number(itinerary.days) || 1, 1) },
          (_, index) => ({
            dayNumber: index + 1,
            date: null,
            title: '',
            cities: (itinerary.cities || []).filter(
              (city: any) =>
                Number(city.dayNumber ?? city.order ?? 1) === index + 1
            )
          })
        )

  const dayPlan = (dayPlanSource as any[]).filter(
    (day: any) => (day.cities || []).length > 0
  )

  return (
    <Container>
      <LoginModal
        isOpen={isModalVisible}
        closeModal={() => setIsModalVisible(false)}
      />

      <ConfirmModal
        isOpen={isDeleteModalOpen}
        title="Excluir roteiro?"
        description={
          <>
            Você tem certeza que deseja excluir{' '}
            <strong>“{itinerary.title}”</strong>? Essa ação não poderá ser
            desfeita e as cidades serão removidas do mapa quando não estiverem
            presentes em outros roteiros.
          </>
        }
        confirmText="Excluir roteiro"
        cancelText="Cancelar"
        loading={isDeleting}
        onConfirm={deleteOwnItinerary}
        onCancel={() => !isDeleting && setIsDeleteModalOpen(false)}
      />

      {isInspirationModalOpen && (
        <S.InspirationOverlay role="presentation">
          <S.InspirationDialog
            role="dialog"
            aria-modal="true"
            aria-label="Crie sua versão deste roteiro"
            onClick={(event) => event.stopPropagation()}
          >
            {inspirationStep === 'form' && (
              <>
                <S.InspirationEyebrow>✨ Crie sua versão</S.InspirationEyebrow>
                <h2>O que faria esta viagem ter mais a sua cara?</h2>
                <S.InspirationIntro>
                  Conte o que quer preservar e o que gostaria de transformar. Se
                  seu pedido mudar dias ou cidades, mostramos a nova estrutura
                  para você confirmar antes de criar.
                </S.InspirationIntro>

                <S.PrototypeNotice>
                  A nova versão será reescrita para você. A Redsterna pode
                  preservar cidades, fatos e ideias da viagem-base, mas não
                  copia literalmente relatos pessoais do autor. A origem do
                  roteiro continuará indicada na sua versão.
                </S.PrototypeNotice>

                <S.InspirationForm>
                  <label>
                    <strong>O que você quer manter desta viagem?</strong>
                    <span>
                      Conte os lugares, o estilo, o ritmo ou as experiências que
                      mais te inspiraram.
                    </span>
                    <textarea
                      value={inspirationPreferences.keep}
                      maxLength={MAX_INSPIRATION_CHARS}
                      placeholder="Ex.: gostei dos bairros, das caminhadas e da ideia de conhecer a cidade com calma..."
                      onChange={(event) =>
                        setInspirationPreferences((current) => ({
                          ...current,
                          keep: event.target.value
                        }))
                      }
                    />
                    <S.InspirationCounter>
                      {inspirationPreferences.keep.length}/
                      {MAX_INSPIRATION_CHARS}
                    </S.InspirationCounter>
                  </label>

                  <label>
                    <strong>O que gostaria de mudar ou acrescentar?</strong>
                    <span>
                      Pode pedir mais cafés, menos museus, viagem com crianças,
                      gastronomia, natureza...
                    </span>
                    <textarea
                      value={inspirationPreferences.change}
                      maxLength={MAX_INSPIRATION_CHARS}
                      placeholder="Ex.: quero menos museus e mais cafés especiais e restaurantes locais..."
                      onChange={(event) =>
                        setInspirationPreferences((current) => ({
                          ...current,
                          change: event.target.value
                        }))
                      }
                    />
                    <S.InspirationCounter>
                      {inspirationPreferences.change.length}/
                      {MAX_INSPIRATION_CHARS}
                    </S.InspirationCounter>
                  </label>

                  <S.InspirationChoiceGroup>
                    <strong>Com quem você vai?</strong>
                    <S.InspirationChoiceRow>
                      {(
                        [
                          ['solo', 'Sozinho'],
                          ['couple', 'Casal'],
                          ['friends', 'Amigos'],
                          ['family', 'Com crianças']
                        ] as const
                      ).map(([value, label]) => (
                        <button
                          key={value}
                          type="button"
                          className={
                            inspirationPreferences.company === value
                              ? 'selected'
                              : ''
                          }
                          onClick={() =>
                            setInspirationPreferences((current) => ({
                              ...current,
                              company: value
                            }))
                          }
                        >
                          {label}
                        </button>
                      ))}
                    </S.InspirationChoiceRow>
                  </S.InspirationChoiceGroup>

                  <S.InspirationChoiceGroup>
                    <strong>Qual o ritmo da viagem?</strong>
                    <S.InspirationChoiceRow>
                      {(
                        [
                          ['slow', 'Tranquilo'],
                          ['balanced', 'Equilibrado'],
                          ['intense', 'Quero aproveitar tudo']
                        ] as const
                      ).map(([value, label]) => (
                        <button
                          key={value}
                          type="button"
                          className={
                            inspirationPreferences.pace === value
                              ? 'selected'
                              : ''
                          }
                          onClick={() =>
                            setInspirationPreferences((current) => ({
                              ...current,
                              pace: value
                            }))
                          }
                        >
                          {label}
                        </button>
                      ))}
                    </S.InspirationChoiceRow>
                  </S.InspirationChoiceGroup>
                </S.InspirationForm>

                <S.InspirationDraftNote>
                  Você pode consultar o roteiro original sem perder o que já
                  escreveu.
                </S.InspirationDraftNote>

                <S.InspirationActions>
                  <button
                    type="button"
                    onClick={() => setIsInspirationModalOpen(false)}
                    disabled={isInspiring}
                  >
                    Ver roteiro original
                  </button>
                  {inspirationStatus?.inspiredItineraryId && (
                    <button
                      type="button"
                      onClick={() => {
                        setIsInspirationModalOpen(false)
                        history.push(
                          `/editar-roteiro/${inspirationStatus.inspiredItineraryId}`
                        )
                      }}
                      disabled={isInspiring}
                    >
                      Abrir versão atual
                    </button>
                  )}
                  <button
                    type="button"
                    className="primary"
                    onClick={createInspiredItinerary}
                    disabled={isInspiring}
                  >
                    ✨ Criar minha versão
                  </button>
                </S.InspirationActions>
              </>
            )}

            {inspirationStep === 'planning' && (
              <S.InspirationProcessing>
                <S.InspirationEyebrow>
                  Entendendo sua viagem
                </S.InspirationEyebrow>
                <h2>Preparando sua personalização...</h2>
                <p>
                  A Redsterna está entendendo o que você quer manter e o que
                  gostaria de transformar.
                </p>
              </S.InspirationProcessing>
            )}

            {inspirationStep === 'structure' && structurePlan && (
              <>
                <S.InspirationEyebrow>Ajuste na viagem</S.InspirationEyebrow>

                {structurePlan.status === 'clarification' ? (
                  <>
                    <h2>
                      {structurePlan.question ||
                        `Como você quer seguir com esta mudança?`}
                    </h2>
                    <S.InspirationIntro>
                      {structurePlan.clarificationType === 'place'
                        ? 'Encontramos mais de uma interpretação possível para o destino. Escolha qual você quis dizer antes de continuarmos.'
                        : structurePlan.clarificationType === 'viability'
                          ? 'A Redsterna encontrou uma limitação prática no plano. Escolha como prefere ajustar a viagem.'
                          : 'Antes de alterar dias ou cidades, escolha como essa mudança deve entrar na viagem.'}
                    </S.InspirationIntro>

                    {structurePlan.clarificationType === 'structure' ? (
                      <S.StructureOptions>
                        <button
                          type="button"
                          className={
                            selectedStructureAction === 'day_trip'
                              ? 'selected'
                              : ''
                          }
                          onClick={() => setSelectedStructureAction('day_trip')}
                        >
                          <strong>Bate-volta</strong>
                          <span>
                            Mantém a duração atual e inclui{' '}
                            {structurePlan.requestedPlace || 'o novo destino'}{' '}
                            no mesmo dia da cidade-base.
                          </span>
                        </button>
                        <button
                          type="button"
                          className={
                            selectedStructureAction === 'replace_day'
                              ? 'selected'
                              : ''
                          }
                          onClick={() =>
                            setSelectedStructureAction('replace_day')
                          }
                        >
                          <strong>Substituir um dia</strong>
                          <span>
                            Mantém a duração da viagem, trocando um dos dias
                            pelo novo destino.
                          </span>
                        </button>
                        <button
                          type="button"
                          className={
                            selectedStructureAction === 'add_day'
                              ? 'selected'
                              : ''
                          }
                          onClick={() => setSelectedStructureAction('add_day')}
                        >
                          <strong>Adicionar um dia</strong>
                          <span>
                            Aumenta a viagem em 1 dia para incluir o novo
                            destino.
                          </span>
                        </button>
                      </S.StructureOptions>
                    ) : (
                      <S.StructureOptions>
                        {(structurePlan.clarificationOptions || []).map(
                          (option) => {
                            const answer = `${option.value} | ${option.label}`
                            return (
                              <button
                                type="button"
                                key={option.value}
                                className={
                                  selectedClarificationAnswer === answer
                                    ? 'selected'
                                    : ''
                                }
                                onClick={() =>
                                  setSelectedClarificationAnswer(answer)
                                }
                              >
                                <strong>{option.label}</strong>
                                <span>{option.description}</span>
                              </button>
                            )
                          }
                        )}
                      </S.StructureOptions>
                    )}
                  </>
                ) : (
                  <>
                    <h2>Confira como ficará sua viagem</h2>
                    <S.InspirationIntro>
                      A Redsterna interpretou seu pedido e montou a estrutura
                      abaixo. Nada será alterado antes da sua confirmação.
                    </S.InspirationIntro>

                    {structurePlan.changes?.length > 0 && (
                      <S.StructureChanges>
                        {structurePlan.changes.map((change, index) => (
                          <span key={`${change}-${index}`}>✓ {change}</span>
                        ))}
                      </S.StructureChanges>
                    )}

                    <S.StructurePlanList>
                      {structurePlan.days.map((day) => (
                        <div key={day.dayNumber}>
                          <strong>Dia {day.dayNumber}</strong>
                          <span>{day.cities.join(' + ')}</span>
                          {day.note && <small>{day.note}</small>}
                        </div>
                      ))}
                    </S.StructurePlanList>

                    <S.StructureConfirmation>
                      <strong>
                        {structurePlan.originalDays ===
                        structurePlan.proposedDays
                          ? `${structurePlan.proposedDays} dias de viagem`
                          : `${structurePlan.originalDays} → ${structurePlan.proposedDays} dias`}
                      </strong>
                      <span>
                        Depois da confirmação, a Redsterna cria o novo dia a dia
                        usando exatamente esta estrutura.
                      </span>
                    </S.StructureConfirmation>
                  </>
                )}

                <S.InspirationActions>
                  <button
                    type="button"
                    onClick={() => setInspirationStep('form')}
                    disabled={isInspiring}
                  >
                    Voltar
                  </button>
                  <button
                    type="button"
                    className="primary"
                    onClick={confirmStructureAndCreate}
                    disabled={isInspiring}
                  >
                    {structurePlan.status === 'clarification'
                      ? 'Continuar'
                      : 'Confirmar e criar'}
                  </button>
                </S.InspirationActions>
              </>
            )}

            {inspirationStep === 'error' && (
              <S.InspirationErrorState>
                <S.InspirationEyebrow>
                  Seu pedido está preservado
                </S.InspirationEyebrow>
                <h2>Não conseguimos concluir esta etapa.</h2>
                <p>
                  {inspirationError ||
                    'Ocorreu uma instabilidade durante a personalização.'}
                </p>
                <span>
                  Você não precisa escrever tudo novamente. Tente outra vez ou
                  volte para ajustar seu pedido.
                </span>
                <S.InspirationActions>
                  <button
                    type="button"
                    onClick={() =>
                      setInspirationStep(structurePlan ? 'structure' : 'form')
                    }
                    disabled={isInspiring}
                  >
                    Revisar pedido
                  </button>
                  <button
                    type="button"
                    className="primary"
                    onClick={retryInspiration}
                    disabled={isInspiring}
                  >
                    Tentar novamente
                  </button>
                </S.InspirationActions>
              </S.InspirationErrorState>
            )}

            {inspirationStep === 'processing' && (
              <S.InspirationProcessing>
                <S.InspirationEyebrow>Montando sua versão</S.InspirationEyebrow>
                <h2>Personalizando sua viagem...</h2>
                <p>
                  A Redsterna está transformando esta inspiração em uma viagem
                  personalizada para você.
                </p>
                <S.InspirationProgress
                  role="progressbar"
                  aria-label="Personalização em andamento"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={inspirationProgress}
                >
                  <span style={{ width: `${inspirationProgress}%` }} />
                  <img
                    src="/images/icon-512.png"
                    alt=""
                    aria-hidden="true"
                    style={{ left: `${inspirationProgress}%` }}
                  />
                </S.InspirationProgress>
                <S.InspirationProgressMeta>
                  <span>{inspirationProgress}%</span>
                  <small>
                    {inspirationProgress < 45
                      ? 'Entendendo suas preferências'
                      : inspirationProgress < 75
                        ? 'Personalizando a viagem'
                        : inspirationProgress < 100
                          ? 'Finalizando seu roteiro'
                          : 'Pronto'}
                  </small>
                </S.InspirationProgressMeta>
              </S.InspirationProcessing>
            )}

            {inspirationStep === 'ready' && (
              <>
                <S.InspirationEyebrow>
                  ✨ Sua versão está pronta
                </S.InspirationEyebrow>
                <h2>Já demos o primeiro passo por você.</h2>
                <S.InspirationIntro>
                  Criamos um rascunho inicial com base nas suas preferências e
                  nas mudanças de viagem que você confirmou.
                </S.InspirationIntro>

                <S.InspirationResultCard>
                  <small>Baseada em</small>
                  <strong>{itinerary.title}</strong>

                  <S.InspirationResultMeta>
                    <span>
                      {structurePlan?.status === 'ready'
                        ? `${structurePlan.proposedDays} dias`
                        : `${dayPlan.length} dias`}
                    </span>
                    <span>{companyLabels[inspirationPreferences.company]}</span>
                    <span>{paceLabels[inspirationPreferences.pace]}</span>
                  </S.InspirationResultMeta>

                  <S.InspirationTagList>
                    {inspirationTags.map((tag) => (
                      <span key={tag}>{tag}</span>
                    ))}
                  </S.InspirationTagList>
                </S.InspirationResultCard>

                <S.PrototypeNotice>
                  Esta versão foi inspirada em “{itinerary.title}”, mas o texto
                  foi reescrito para as suas preferências. Relatos pessoais do
                  autor não são copiados literalmente e a origem permanece
                  vinculada ao roteiro.
                </S.PrototypeNotice>

                <S.InspirationActions>
                  <button
                    type="button"
                    onClick={() => setIsInspirationModalOpen(false)}
                  >
                    Ver depois
                  </button>
                  <button
                    type="button"
                    className="primary"
                    onClick={openInspiredDraft}
                  >
                    Abrir meu roteiro
                  </button>
                </S.InspirationActions>
              </>
            )}
          </S.InspirationDialog>
        </S.InspirationOverlay>
      )}

      {lightbox && (
        <S.LightboxOverlay onClick={closeLightbox} role="presentation">
          <S.LightboxDialog
            role="dialog"
            aria-modal="true"
            aria-label={`Fotos de ${lightbox.cityName}`}
            onClick={(event) => event.stopPropagation()}
          >
            <S.LightboxClose
              type="button"
              aria-label="Fechar galeria"
              onClick={closeLightbox}
            >
              ×
            </S.LightboxClose>
            {lightbox.images.length > 1 && (
              <S.LightboxArrow
                type="button"
                aria-label="Foto anterior"
                $side="left"
                onClick={() => changeLightboxImage(-1)}
              >
                ‹
              </S.LightboxArrow>
            )}
            <S.LightboxImage
              src={lightbox.images[lightbox.currentIndex]}
              alt={`${lightbox.cityName} - foto ${lightbox.currentIndex + 1}`}
            />
            {lightbox.images.length > 1 && (
              <S.LightboxArrow
                type="button"
                aria-label="Próxima foto"
                $side="right"
                onClick={() => changeLightboxImage(1)}
              >
                ›
              </S.LightboxArrow>
            )}
            <S.LightboxCounter>
              {lightbox.currentIndex + 1} / {lightbox.images.length}
            </S.LightboxCounter>
          </S.LightboxDialog>
        </S.LightboxOverlay>
      )}

      <Header cover={itinerary.coverImage} />
      <Line fullWidth>
        <Content>
          <S.ItineraryHeader>
            <S.HeaderRow>
              <S.HeaderIcon src="/images/explore-icon.png" />
              <div>
                <S.Tittle>{itinerary.title}</S.Tittle>
                {itinerary.inspirationSource && (
                  <S.InspirationSource>
                    Inspirado em{' '}
                    <S.InspirationSourceLink
                      to={getItineraryPublicPath({
                        id: itinerary.inspirationSource.id
                      })}
                    >
                      {itinerary.inspirationSource.title}
                    </S.InspirationSourceLink>
                  </S.InspirationSource>
                )}
                {itinerary.author && (
                  <S.AuthorName
                    to={{ pathname: `/usuario/${itinerary.author.id}` }}
                  >
                    <span>{itinerary.author.firstname} </span>
                    <span>{itinerary.author.lastname} </span>
                  </S.AuthorName>
                )}
              </div>
            </S.HeaderRow>

            <S.Icons>
              <StarRating
                value={displayedRate}
                readOnly={isOwnItinerary}
                onClick={(value: number) =>
                  onRateItinerary({ id: itinerary.id, rate: value })
                }
              />
              {isOwnItinerary && (
                <S.OwnerActions>
                  <S.OwnerActionButton
                    type="button"
                    onClick={() =>
                      history.push(`/editar-roteiro/${itinerary.id}`)
                    }
                  >
                    Editar roteiro
                  </S.OwnerActionButton>
                  <S.OwnerActionButton
                    type="button"
                    $danger
                    disabled={isDeleting}
                    onClick={() => setIsDeleteModalOpen(true)}
                  >
                    {isDeleting ? 'Excluindo...' : 'Excluir roteiro'}
                  </S.OwnerActionButton>
                </S.OwnerActions>
              )}
              {authenticated && !isOwnItinerary && (
                <S.Heart
                  color={isFavorited ? '#c80000' : '#bfbfbf'}
                  onClick={() => onFavoriteItinerary(itinerary.id)}
                  size={40}
                />
              )}
              {itinerary.authorId !== user?.id && (
                <S.InspireButton type="button" onClick={requestInspiration}>
                  {hasInspirationDraft
                    ? '✨ Continuar minha versão'
                    : inspirationStatus?.inspiredItineraryId
                      ? '✨ Refinar minha versão'
                      : '✨ Torne este roteiro seu'}
                </S.InspireButton>
              )}
            </S.Icons>
          </S.ItineraryHeader>

          <S.InfosWrapper>
            <S.InfoCard>
              <S.InfoIcon src="/images/calendar-icon.svg" />
              <div>
                <S.InfoText>Duração</S.InfoText>
                <S.BoldText>
                  {dayPlan.length} {dayPlan.length === 1 ? 'dia' : 'dias'}
                </S.BoldText>
              </div>
            </S.InfoCard>
            <S.InfoCard>
              <div>
                <S.InfoText>Status</S.InfoText>
                <S.BoldText>
                  {itinerary.inspirationSource ? (
                    <S.InspirationSourceLink
                      to={getItineraryPublicPath({
                        id: itinerary.inspirationSource.id
                      })}
                    >
                      Roteiro Inspirado
                    </S.InspirationSourceLink>
                  ) : itinerary.isEditorial ? (
                    'Editorial Redsterna'
                  ) : itinerary.made ? (
                    'Viagem realizada'
                  ) : (
                    'Roteiro planejado'
                  )}
                </S.BoldText>
              </div>
            </S.InfoCard>
          </S.InfosWrapper>

          {hasSummary && (
            <S.Summary>
              <S.InfoCard>
                <S.SummaryTitle>
                  <span>Resumo da </span> <span>viagem</span>
                </S.SummaryTitle>
              </S.InfoCard>
              <S.DescriptionCard>{itinerary.summary}</S.DescriptionCard>
            </S.Summary>
          )}

          <S.ReadSection>
            <S.ReadSectionHeading>
              <div>
                <strong>Dia a dia da viagem</strong>
                <span>
                  {itinerary.made
                    ? 'Cidades, experiências e fotos organizadas por dia.'
                    : 'A estrutura planejada desta viagem, dia por dia.'}
                </span>
              </div>
            </S.ReadSectionHeading>

            <S.ReadDays>
              {dayPlan.map((day: any, dayIndex: number) => (
                <S.ReadDayCard key={day.id || dayIndex}>
                  <S.ReadDayHeader>
                    <S.ReadDayNumber>Dia {dayIndex + 1}</S.ReadDayNumber>
                    {day.date && (
                      <S.ReadDayDate>
                        {new Date(
                          `${String(day.date).slice(0, 10)}T12:00:00`
                        ).toLocaleDateString('pt-BR')}
                      </S.ReadDayDate>
                    )}
                    {day.title && <strong>{day.title}</strong>}
                  </S.ReadDayHeader>

                  <S.ReadDayCities>
                    {(day.cities || []).map((city: any, cityIndex: number) => {
                      const cityImages =
                        city?.images?.map(
                          ({ file }: { file: string }) => file
                        ) ?? []

                      return (
                        <S.ReadCityCard key={city.id || cityIndex}>
                          <S.ReadCityHeader>
                            <span>Cidade {cityIndex + 1}</span>
                            <strong>{city.name}</strong>
                          </S.ReadCityHeader>

                          {city.description ? (
                            <S.SummaryDescription>
                              <S.DescriptionText>
                                {city.description}
                              </S.DescriptionText>
                            </S.SummaryDescription>
                          ) : null}

                          {cityImages.length > 0 && (
                            <S.ImageWrapper>
                              <S.ImageContainer>
                                {cityImages
                                  .slice(0, 6)
                                  .map((image: string, imageIndex: number) => (
                                    <S.LightboxWrapper key={image}>
                                      <S.LightboxButton
                                        type="button"
                                        aria-label={`Abrir foto ${
                                          imageIndex + 1
                                        } de ${city.name}`}
                                        onClick={() =>
                                          setLightbox({
                                            images: cityImages,
                                            currentIndex: imageIndex,
                                            cityName: city.name
                                          })
                                        }
                                      >
                                        <S.Photos src={image} alt={city.name} />
                                      </S.LightboxButton>
                                    </S.LightboxWrapper>
                                  ))}
                              </S.ImageContainer>
                            </S.ImageWrapper>
                          )}
                        </S.ReadCityCard>
                      )
                    })}

                    {(day.cities || []).length === 0 && (
                      <S.EmptyDay>
                        Nenhuma cidade foi adicionada a este dia.
                      </S.EmptyDay>
                    )}
                  </S.ReadDayCities>
                </S.ReadDayCard>
              ))}
            </S.ReadDays>
          </S.ReadSection>

          <ItineraryComments
            itineraryId={itinerary.id}
            itineraryAuthorId={itinerary.authorId}
            onRequireLogin={() => setIsModalVisible(true)}
          />
        </Content>
      </Line>
    </Container>
  )
}
